(function () {
  const isMetaInAppBrowser = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|FB4A|Threads/i.test(navigator.userAgent || '');

  if (isMetaInAppBrowser) {
    const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
    viewTransitionRenderBlocker?.remove();
    document.documentElement.classList.add('zena-meta-inapp-browser');

    if (!document.getElementById('zena-meta-inapp-view-transition-fix-style')) {
      const style = document.createElement('style');
      style.id = 'zena-meta-inapp-view-transition-fix-style';
      style.textContent = '@view-transition { navigation: none; } ::view-transition { pointer-events: none; }';
      document.head.appendChild(style);
    }

    const cleanupDrawerState = () => {
      document.documentElement.removeAttribute('scroll-lock');

      if (document.body) {
        const lockedTop = document.body.style.top;
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';

        if (lockedTop && lockedTop.startsWith('-')) {
          const scrollY = Number.parseInt(lockedTop, 10) * -1;
          if (Number.isFinite(scrollY)) {
            window.scrollTo({ top: scrollY, behavior: 'instant' });
          }
        }
      }

      document
        .querySelectorAll('header-drawer details[open], .header__drawer details[open], details.menu-drawer-container[open]')
        .forEach((details) => {
          details.removeAttribute('open');
          details.classList.remove('menu-open');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        });

      document.querySelectorAll('.menu-drawer--has-submenu-opened').forEach((element) => {
        element.classList.remove('menu-drawer--has-submenu-opened');
      });

      document.querySelectorAll('summary[aria-expanded="true"]').forEach((summary) => {
        if (summary.closest('header-drawer, .header__drawer, .menu-drawer')) {
          summary.setAttribute('aria-expanded', 'false');
        }
      });
    };

    window.addEventListener('pageshow', () => setTimeout(cleanupDrawerState, 0));

    document.addEventListener(
      'click',
      (event) => {
        if (event.defaultPrevented) return;
        if (event instanceof MouseEvent && event.button !== 0) return;
        if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
        if (!(event.target instanceof Element)) return;

        const link = event.target.closest('header-drawer a[href], .header__drawer a[href], .menu-drawer a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;
        if (link.target === '_blank' || link.hasAttribute('download')) return;

        const rawHref = link.getAttribute('href') || '';
        if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

        let url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (_error) {
          return;
        }

        if (url.origin !== window.location.origin) return;
        if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

        event.preventDefault();
        cleanupDrawerState();
        window.setTimeout(() => window.location.assign(url.href), 25);
      },
      true
    );

    return;
  }

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
